 
import { useState } from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCaretDown, faCaretRight, faPlus, faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { IProFormaUses, DevelopmentUnitOfMeasureTypes, IFormattedCashFlowPeriod } from 'views/ProFormaTable/types';
import { NSCheckbox } from 'bricks';
import { numeralFormatterCurrency } from 'ns_libs/formatter';
import NSCellInput from 'bricks/NSCellInput/NSCellInput';
import RenderIf from 'components/RenderIf/RenderIf';
import { PRO_FORMA_UNIT_TO_FIELD_VALUES } from 'constants/unitTypes';
import { DEFAULT_BUDGET_LINE_NAME } from 'views/ProFormaTable/constants';
import { useProFormaSocketContext } from 'views/ProFormaTable/components/ProForma/socketContext/ProFormaSocketProvider';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import { Periods } from 'components/ProFormaReturnsTable/constant';
import { useUsesTableContext } from '../../context/UsesTableProvider';
import UsesTableBudgetLine from './UsesTableBudgetLine/UsesTableBudgetLine';
import PeriodCellValue from '../../PeriodCellValue/PeriodCellValue';

export interface IUsesTableSubcategory {
    subcategory: IProFormaUses;
    unitType: DevelopmentUnitOfMeasureTypes;
    projectionPeriods: IFormattedCashFlowPeriod[];
    isPreview?: boolean;
}

const UsesTableSubcategory = ({
    subcategory, unitType, projectionPeriods, isPreview,
}: IUsesTableSubcategory) => {
    const {
        selectedCategories, expandedCategories, updateSelectedCategories,
        updateExpandedCategories, isProFormaReadOnly,
    } = useUsesTableContext();

    const {
        handleCreateBudgetClassification,
        isBudgetClassificationLoading,
        handleUpdateBudgetClassifications,
        handleDeleteBudgetClassification,
        isBudgetClassificationDeletionLoading,
        showCents,
        periodCadence,
    } = useProFormaSocketContext();

    const [isHovered, setIsHovered] = useState(false);
    const [openDeleteSubcategoryModal, setOpenDeleteSubcategoryModal] = useState(false);

    const toggleDeleteSubcategoryModal = () => setOpenDeleteSubcategoryModal(!openDeleteSubcategoryModal);

    const editableBudgetClassificationIds = subcategory.children?.map(
        budgetClassification => (budgetClassification.isDeletable ? budgetClassification.id : null),
    ).filter(id => id !== null) || [];

    const expandCategory = () => {
        if (!expandedCategories.subcategories.includes(subcategory.id)) {
            updateExpandedCategories(null, subcategory.id);
        }
    };

    const createBudgetLine = () => {
        const postBody = {
            budgetLineNickname: DEFAULT_BUDGET_LINE_NAME,
            parentId: subcategory.id,
        };
        handleCreateBudgetClassification(postBody, expandCategory);
    };

    const deleteSubcategory = () => {
        handleDeleteBudgetClassification([subcategory.id], false, toggleDeleteSubcategoryModal);
    };

    const selectedUnitTotal = subcategory[PRO_FORMA_UNIT_TO_FIELD_VALUES[unitType]];
    const hasNoSelectedCategories = Object.values(selectedCategories).every(categoryLevel => !categoryLevel.length);

    let subcategoryPeriods = subcategory.periods || [];
    if (periodCadence === Periods.ANNUALLY) subcategoryPeriods = subcategory.yearlyPeriods || [];

    return (
        <>
            <tr
                className="NSTable__tbody__tr NSTable__tbody__tr--level-2"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <td
                    className="NSTable__tbody__tr__td NSTable__tbody__tr__td--right-border NSTable__tbody__tr__td__input text-dark"
                >
                    <div className="d-flex justify-content-between">
                        <div className="d-flex align-items-center">
                            <RenderIf isTrue={!isProFormaReadOnly && !isPreview}>
                                <NSCheckbox
                                    id={`selectSubcategory-${subcategory.id}`}
                                    checked={selectedCategories.subcategories.includes(subcategory.id)}
                                    callback={() => updateSelectedCategories(null, [subcategory.id], editableBudgetClassificationIds as number[])}
                                    containerClassName="custom-control-inline align-middle"
                                />
                            </RenderIf>
                            <div className="d-flex align-items-center">
                                <RenderIf isTrue={Boolean(subcategory.children?.length)}>
                                    <FontAwesomeIcon
                                        className="icon ml-1 mr-2 ReadOnlyWrapper--enable-pointer-events"
                                        role="presentation"
                                        onClick={() => updateExpandedCategories(null, subcategory.id)}
                                        icon={expandedCategories.subcategories.includes(subcategory.id) ? faCaretDown : faCaretRight}
                                        fixedWidth
                                    />
                                </RenderIf>
                                <NSCellInput
                                    value={subcategory.description}
                                    handleUpdateValue={updatedValue => {
                                        handleUpdateBudgetClassifications([{ id: subcategory.id, description: updatedValue as string }]);
                                    }}
                                    placeholder="Enter subcategory name"
                                    customClassName={subcategory.children?.length ? '' : 'ml-4'}
                                    disabled={isPreview}
                                />
                            </div>
                        </div>
                        <div className="d-flex flex-row flex-nowrap align-items-center">
                            <RenderIf isTrue={hasNoSelectedCategories}>
                                <FontAwesomeIcon
                                    icon={faTrash}
                                    onClick={() => isHovered && toggleDeleteSubcategoryModal()}
                                    className={classNames('text-muted cursor--pointer hover:text-dark', { invisible: !isHovered })}
                                />
                            </RenderIf>

                            <RenderIf isTrue={isHovered}>
                                <RenderIf isTrue={isBudgetClassificationLoading}>
                                    <span className="spinner-border spinner-border-sm text-muted ml-1" style={{ height: '15px', width: '15px' }} />
                                </RenderIf>
                                <RenderIf isTrue={!isBudgetClassificationLoading}>
                                    <FontAwesomeIcon
                                        icon={faPlus}
                                        onClick={createBudgetLine}
                                        className={classNames('pl-2 text-muted cursor--pointer hover:text-dark')}
                                    />
                                </RenderIf>
                            </RenderIf>
                        </div>
                    </div>
                </td>
                <td className="NSTable__tbody__tr__td" />
                <td className="NSTable__tbody__tr__td" />
                <td className="NSTable__tbody__tr__td" />
                <td className="NSTable__tbody__tr__td">
                    <div className="d-flex justify-content-end text-dark">
                        {selectedUnitTotal != null ? numeralFormatterCurrency(selectedUnitTotal) : '—'}
                    </div>
                </td>
                <td className="NSTable__tbody__tr__td NSTable__tbody__tr__td--right-border pr-2">
                    <div className="d-flex justify-content-end text-dark">
                        {subcategory.amount != null ? numeralFormatterCurrency(subcategory.amount, showCents ? '$0,0.00' : '$0,0') : '—'}
                    </div>
                </td>
                {projectionPeriods?.map((period, index) => {
                    const targetPeriod = subcategoryPeriods[index];

                    return (
                        <PeriodCellValue
                            key={`uses-subcategory-period-${period.value}`}
                            tooltipId={`uses-subcategory-period-${index}`}
                            period={period}
                            value={targetPeriod?.projected === null || subcategory.amount === null
                                ? '—' : numeralFormatterCurrency(targetPeriod?.projected, showCents ? '$0,0.00' : '$0,0')}
                            cellClassName="NSTable__tbody__tr__td text-dark text-right"
                        />
                    );
                })}
            </tr>

            <RenderIf isTrue={expandedCategories.subcategories.includes(subcategory.id)}>
                {(subcategory.children || []).map(budgetClassification => (
                    <UsesTableBudgetLine
                        key={budgetClassification.id}
                        budgetClassification={budgetClassification}
                        unitType={unitType}
                        projectionPeriods={projectionPeriods}
                        isPreview={isPreview}
                    />
                ))}
            </RenderIf>

            <RenderIf isTrue={openDeleteSubcategoryModal}>
                <ConfirmationModal
                    toggle={toggleDeleteSubcategoryModal}
                    isOpen={openDeleteSubcategoryModal}
                    onConfirm={deleteSubcategory}
                    onCancel={toggleDeleteSubcategoryModal}
                    modalHeaderText={`Delete ${subcategory.description}`}
                    modalFooterButtonText="Yes, delete"
                    modalFooterButtonIsLoading={isBudgetClassificationDeletionLoading}
                    danger
                >
                    <div className="text-dark font-weight-bold mb-1">
                        Are you sure you want to delete
                        {subcategory.description}
                        ?
                    </div>
                    <div>
                        Your work will not be able to be recovered. Any child content within
                        {subcategory.description}
                        {' '}
                        will also be deleted.
                    </div>
                </ConfirmationModal>
            </RenderIf>
        </>
    );
};

export default UsesTableSubcategory;
